import type { PaymentStatus, PaymentsListResponse } from "@teamna/shared";
import { useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  Badge,
  Button,
  DataTable,
  EmptyState,
  ErrorState,
  FactRow,
  Field,
  InlineError,
  Notice,
  Panel,
  Tabs,
  Toolbar,
  panelId,
  showToast,
  tabId,
  type BadgeTone,
  type DataTableColumn,
} from "../components/index.ts";
import { useLabels } from "@teamna/web-client/hooks/use-labels.ts";
import { useActiveBusiness, useOwnerPayments } from "@teamna/web-client/hooks/use-owner.ts";
import { newIdempotencyKey } from "@teamna/web-client/api/client.ts";
import { useRefundPayment } from "@teamna/web-client/hooks/use-owner-settings.ts";
import { usePayoutAccount } from "@teamna/web-client/hooks/use-payout-account.ts";
import { dayKey, formatDate, formatDateTime, formatMoney } from "@teamna/web-client/lib/format.ts";
import { RecurringFeesBody } from "./fees.tsx";
import { CardPaymentsComingSoon } from "./card-payments-coming-soon.tsx";
import { getPaidHeadline } from "./get-paid.tsx";
import { OfficePage } from "./office.tsx";
import { ProofEditor, ProofLink } from "./parent/proof-field.tsx";

/**
 * The money page (web-app-v2.md slice E).
 *
 * Two tabs. Payments is the register of money: card payments, and the cash and bank transfers the
 * owner recorded on a member profile. A table with the date, who paid, what for, the amount, the
 * method and the status, filtered by status and by a date range, and a row opens the details with
 * the refund action. Recurring fees is the standing-fee view the
 * swimming pilot runs on, unchanged.
 *
 * Filters run in the browser over the newest 200 payments. A pilot business takes a few dozen a
 * month, so the whole register is one request and filtering never waits on the network. Dates are
 * the business's own calendar day, not the browser's.
 *
 * Owner only: the API refuses money to an admin or staff, so the page says so instead of failing.
 */

type Row = PaymentsListResponse["items"][number];
type StatusFilter = "all" | "paid" | "pending" | "refunded" | "failed";

const STATUS: Record<PaymentStatus, { label: string; tone: BadgeTone; filter: StatusFilter }> = {
  succeeded: { label: "Paid", tone: "confirmed", filter: "paid" },
  partially_refunded: { label: "Part refunded", tone: "late", filter: "refunded" },
  refunded: { label: "Refunded", tone: "quiet", filter: "refunded" },
  pending: { label: "Waiting", tone: "late", filter: "pending" },
  failed: { label: "Failed", tone: "cancelled", filter: "failed" },
};

function statusOf(row: Row) {
  return STATUS[row.status] ?? { label: row.status, tone: "quiet" as BadgeTone, filter: "all" as StatusFilter };
}

const METHOD_LABEL: Record<Row["method"], string> = { card: "Card", cash: "Cash", transfer: "Bank transfer" };

/** Card from the online checkout; cash and bank transfers the owner recorded on a member profile. */
function methodOf(row: Row): string {
  return METHOD_LABEL[row.method] ?? "Card";
}

function whenOf(row: Row): string {
  return row.paidAt ?? row.createdAt;
}

/** Stripe's fee on a card payment, as the owner reads it. Cash and transfers have none. */
function feeOf(row: Row): string {
  if (row.method !== "card") return "None";
  return row.gatewayFeeFils === null ? "Not known yet" : formatMoney(row.gatewayFeeFils, row.currency);
}

function refundableFils(row: Row): number {
  if (row.status !== "succeeded" && row.status !== "partially_refunded") return 0;
  return Math.max(0, row.amountFils - row.refundedFils);
}

export function OwnerPaymentsScreen() {
  const [tab, setTab] = useState<"payments" | "fees">("payments");

  return (
    <OfficePage title="Payments" description="Card payments, cash and bank transfers, and the standing fees.">
      {(business) =>
        business.staffRole === "frontdesk" ? (
          // The front desk (T1) reads the register: who paid, what, how. No refund, no card payments
          // set-up, no recurring fees (the owner's).
          <PaymentsRegister businessId={business.businessId} timezone={business.timezone} canRefund={false} />
        ) : business.staffRole !== "owner" ? (
          <EmptyState
            title="Owner only"
            message="Payments are visible to the business owner. Ask them if you need a figure."
          />
        ) : (
          <>
            <Tabs
              label="Payments views"
              tabs={[
                { id: "payments", label: "Payments" },
                { id: "fees", label: "Recurring fees" },
              ]}
              selectedId={tab}
              onSelect={(id) => setTab(id as "payments" | "fees")}
            />
            <div role="tabpanel" id={panelId(tab)} aria-labelledby={tabId(tab)}>
              {tab === "payments" ? (
                <>
                  {business.cardPaymentsComingSoon ? (
                    <div className="mb-5">
                      <CardPaymentsComingSoon />
                    </div>
                  ) : (
                    <CardPaymentsBox businessId={business.businessId} timezone={business.timezone} />
                  )}
                  <PaymentsRegister businessId={business.businessId} timezone={business.timezone} />
                </>
              ) : business.cardPaymentsComingSoon ? (
                // A recurring fee is paid by card in the app, so the tab waits for card payments too.
                <CardPaymentsComingSoon title="Recurring fees are coming soon" />
              ) : (
                <RecurringFeesBody businessId={business.businessId} />
              )}
            </div>
          </>
        )
      }
    </OfficePage>
  );
}

/**
 * Card payments in one box above the register: on, off, or what Stripe still needs, with the way to
 * the Get paid page. Verified with nothing to do is one quiet line.
 */
function CardPaymentsBox({ businessId, timezone }: { businessId: string; timezone: string }) {
  const navigate = useNavigate();
  const { label } = useLabels();
  const account = usePayoutAccount(businessId);
  if (!account.data) return null;
  const data = account.data;
  const open = (
    <Button variant="secondary" readOnlySafe onClick={() => void navigate({ to: "/owner/setup/get-paid" })}>
      {data.status === "not_started" ? "Set up card payments" : "Open Get paid"}
    </Button>
  );
  if (data.provider === "fake") {
    // The honest stub: true on every environment that has no Stripe yet, production included.
    return (
      <p className="mb-4 text-[14px] text-ink-2">
        Card payments are not connected yet. The card checkout is a test page and no money moves.
      </p>
    );
  }
  if (data.status === "complete" && data.account?.payoutInterval !== "manual") {
    return (
      <p className="mb-4 text-[14px] text-ink-2">
        Card payments are on.{data.account?.bankLast4 ? ` Payouts go to the IBAN ending in ${data.account.bankLast4}.` : ""}{" "}
        <Button variant="ghost" readOnlySafe onClick={() => void navigate({ to: "/owner/setup/get-paid" })}>
          Get paid settings
        </Button>
      </p>
    );
  }
  const headline = getPaidHeadline(data, timezone, label("member", { plural: true, capital: true }));
  const manual = data.status === "complete";
  return (
    <div className="mb-5">
      <Notice
        tone={manual ? "warning" : headline.tone}
        title={manual ? "Payouts are manual" : headline.title}
        action={open}
      >
        {manual ? "Stripe sends no money to your bank by itself. Set the payout schedule in your Stripe dashboard." : headline.body}
      </Notice>
    </div>
  );
}

/** The register's columns. `memberWord` heads the payer column in the business's own word. */
/** The register's columns. A parent's page leaves out who paid: it is always that family. */
export function paymentColumns(timezone: string, memberWord: string, options: { withPayer?: boolean } = {}): DataTableColumn<Row>[] {
  const columns: DataTableColumn<Row>[] = [
    {
      id: "date",
      header: "Date",
      sortValue: (row) => whenOf(row),
      cell: (row) => <span className="whitespace-nowrap">{formatDate(whenOf(row), timezone)}</span>,
    },
    {
      id: "member",
      header: memberWord,
      sortValue: (row) => row.payerName,
      cell: (row) => <span className="font-semibold">{row.payerName ?? "Not known"}</span>,
    },
    {
      id: "what",
      header: "What",
      hideOnPhone: true,
      sortValue: (row) => row.what,
      cell: (row) => row.what ?? "Not recorded",
    },
    {
      id: "amount",
      header: "Amount",
      align: "end",
      sortValue: (row) => row.amountFils,
      cell: (row) => (
        <span className="font-semibold whitespace-nowrap tabular-nums">
          {formatMoney(row.amountFils, row.currency)}
        </span>
      ),
    },
    {
      id: "fee",
      header: "Stripe fee",
      align: "end",
      hideOnPhone: true,
      cell: (row) => <span className="whitespace-nowrap tabular-nums text-ink-2">{feeOf(row)}</span>,
    },
    { id: "method", header: "Method", hideOnPhone: true, cell: methodOf },
    {
      // The proof of a cash or transfer payment (migration 0084): a paper clip that opens the file.
      id: "proof",
      header: "Proof",
      cell: (row) => (row.proof ? <ProofLink proof={row.proof} compact /> : null),
    },
    {
      id: "status",
      header: "Status",
      sortValue: (row) => statusOf(row).label,
      cell: (row) => <Badge tone={statusOf(row).tone}>{statusOf(row).label}</Badge>,
    },
  ];
  return options.withPayer === false ? columns.filter((column) => column.id !== "member") : columns;
}

function PaymentsRegister({
  businessId,
  timezone,
  canRefund = true,
}: {
  businessId: string;
  timezone: string;
  /** False for the front desk: the panel shows the facts only. */
  canRefund?: boolean;
}) {
  const { label } = useLabels();
  const payments = useOwnerPayments(businessId, true);
  const [status, setStatus] = useState<StatusFilter>("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);

  const all = useMemo(() => payments.data?.items ?? [], [payments.data]);
  const rows = useMemo(
    () =>
      all.filter((row) => {
        if (status !== "all" && statusOf(row).filter !== status) return false;
        const day = dayKey(whenOf(row), timezone);
        if (from && day < from) return false;
        if (to && day > to) return false;
        return true;
      }),
    [all, status, from, to, timezone],
  );

  const counts = useMemo(() => {
    const tally: Record<StatusFilter, number> = { all: all.length, paid: 0, pending: 0, refunded: 0, failed: 0 };
    for (const row of all) {
      const filter = statusOf(row).filter;
      if (filter !== "all") tally[filter] += 1;
    }
    return tally;
  }, [all]);

  const memberWord = label("member", { capital: true });

  const columns = paymentColumns(timezone, memberWord);

  const open = all.find((row) => row.id === openId) ?? null;
  const filtered = status !== "all" || from !== "" || to !== "";

  if (payments.error && !payments.data) {
    return <ErrorState error={payments.error} onRetry={() => void payments.refetch()} />;
  }

  return (
    <>
      <Toolbar
        filters={{
          label: "Filter by status",
          selectedId: status,
          onSelect: (id) => setStatus(id as StatusFilter),
          options: [
            { id: "all", label: "All", count: counts.all },
            { id: "paid", label: "Paid", count: counts.paid },
            { id: "pending", label: "Waiting", count: counts.pending },
            { id: "refunded", label: "Refunded", count: counts.refunded },
            { id: "failed", label: "Failed", count: counts.failed },
          ],
        }}
      >
        <div className="flex items-end gap-2">
          <div className="w-[150px]">
            <Field label="From" type="date" value={from} max={to || undefined} onChange={(event) => setFrom(event.target.value)} />
          </div>
          <div className="w-[150px]">
            <Field label="To" type="date" value={to} min={from || undefined} onChange={(event) => setTo(event.target.value)} />
          </div>
        </div>
      </Toolbar>

      <DataTable
        caption="Payments"
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        rowLabel={(row) => `${row.payerName ?? "Payment"}, ${formatMoney(row.amountFils, row.currency)}`}
        onRowClick={(row) => setOpenId(row.id)}
        isLoading={payments.isLoading}
        defaultSort={{ id: "date", direction: "desc" }}
        countLabel={(count) =>
          filtered ? `${count} of ${all.length} payments` : `${count} ${count === 1 ? "payment" : "payments"}`
        }
        empty={
          filtered
            ? {
                title: "No payments match",
                message: "Change the status or the dates to see more.",
                action: (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setStatus("all");
                      setFrom("");
                      setTo("");
                    }}
                  >
                    Clear the filters
                  </Button>
                ),
              }
            : {
                title: "No payments yet",
                message: `When a ${label("member")} pays in the app, or you record cash or a bank transfer on their profile, the payment shows here.`,
              }
        }
      />

      {open ? (
        <PaymentPanel
          key={open.id}
          row={open}
          businessId={businessId}
          timezone={timezone}
          memberWord={memberWord}
          canRefund={canRefund}
          onClose={() => setOpenId(null)}
        />
      ) : null}
    </>
  );
}

/**
 * One payment: the facts, and the refund where the payment can take one.
 *
 * The refund asks first and quotes the amount on the button. Left blank, the amount is what is
 * still refundable; the API refuses more than that, so the field says the ceiling up front.
 */
function PaymentPanel({
  row,
  businessId,
  timezone,
  memberWord,
  canRefund,
  onClose,
}: {
  row: Row;
  businessId: string;
  timezone: string;
  memberWord: string;
  canRefund: boolean;
  onClose: () => void;
}) {
  const refund = useRefundPayment(businessId);
  const { may } = useActiveBusiness();
  // One key per opening of the panel: a second press after a lost answer replays the first refund.
  const [idempotencyKey] = useState(() => newIdempotencyKey());
  const [refunding, setRefunding] = useState(false);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");

  const left = refundableFils(row);
  const amountFils = amount.trim() === "" ? left : Math.round(Number(amount) * 100);
  const validAmount = Number.isFinite(amountFils) && amountFils >= 1 && amountFils <= left;
  const status = statusOf(row);

  function submit() {
    if (!validAmount) return;
    refund.mutate(
      {
        paymentId: row.id,
        idempotencyKey,
        ...(amountFils !== left ? { amountFils } : {}),
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      },
      {
        onSuccess: () => {
          showToast(`${formatMoney(amountFils, row.currency)} refunded to ${row.payerName ?? "the payer"}`);
          onClose();
        },
      },
    );
  }

  return (
    <Panel
      open
      onClose={onClose}
      title={row.payerName ?? "Payment"}
      subtitle={`${formatMoney(row.amountFils, row.currency)} on ${formatDate(whenOf(row), timezone)}`}
      actions={
        canRefund && left > 0 ? (
          refunding ? (
            <>
              <Button variant="danger" disabled={!validAmount} loading={refund.isPending} onClick={submit}>
                {validAmount ? `Refund ${formatMoney(amountFils, row.currency)}` : "Refund"}
              </Button>
              <Button variant="secondary" onClick={() => setRefunding(false)}>
                Back
              </Button>
            </>
          ) : (
            <Button variant="secondary" onClick={() => setRefunding(true)}>
              Refund
            </Button>
          )
        ) : undefined
      }
    >
      {refund.error ? <InlineError error={refund.error} /> : null}

      <FactRow label={memberWord} value={row.payerName ?? "Not known"} />
      <FactRow label="What" value={row.what ?? "Not recorded"} />
      <FactRow label="Amount" value={formatMoney(row.amountFils, row.currency)} />
      {row.method === "card" ? <FactRow label="Stripe fee" value={feeOf(row)} /> : null}
      {row.refundedFils > 0 ? (
        <FactRow label="Refunded" value={formatMoney(row.refundedFils, row.currency)} />
      ) : null}
      <FactRow label="Method" value={methodOf(row)} />
      <FactRow label="Status" value={status.label} />
      <FactRow label="Paid" value={row.paidAt ? formatDateTime(row.paidAt, timezone) : "Not yet"} />
      {row.method === "card" ? (
        <FactRow label="Reference" value={row.providerPaymentId} last />
      ) : (
        <>
          <FactRow label="Recorded by" value={row.recordedByName ?? "Not known"} />
          {row.note ? <FactRow label="Note" value={row.note} typed /> : null}
          {/* The owner and the admins attach, replace or remove it; the front desk only opens it. */}
          <ProofEditor businessId={businessId} paymentId={row.id} proof={row.proof} canChange={may("changePaymentProof")} />
        </>
      )}

      {refunding && left > 0 ? (
        <div className="mt-5 flex flex-col gap-4">
          <Field
            label="Amount to refund, AED"
            inputMode="decimal"
            value={amount}
            placeholder={String(left / 100)}
            onChange={(event) => setAmount(event.target.value.replace(/[^0-9.]/g, ""))}
            hint={`Leave blank to refund all that is left: ${formatMoney(left, row.currency)}.`}
            error={amount !== "" && !validAmount ? `At most ${formatMoney(left, row.currency)}` : undefined}
          />
          <Field
            label="Reason"
            value={reason}
            maxLength={500}
            placeholder="Optional"
            onChange={(event) => setReason(event.target.value)}
          />
          <p className="text-[13px] text-ink-2">
            {row.method === "card"
              ? `The money goes back to the card that paid. Stripe keeps its fee${row.gatewayFeeFils !== null ? ` of ${formatMoney(row.gatewayFeeFils, row.currency)}` : ""}, so the refund costs you that fee.`
              : "You give the money back yourself. This only records the refund."}{" "}
            The sessions it bought stay on the account. Change them from the {memberWord.toLowerCase()} profile
            if they should go too.
          </p>
        </div>
      ) : null}
    </Panel>
  );
}
